import { useState, useRef, useEffect, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import { activityService } from "../../services/activityService";
import { useI18n } from "../../i18n/LanguageContext";
import Icon from "../Common/Icon";

// label alanları i18n anahtarlarıdır; render sırasında t() ile çevrilir.
// Kategori (children'lı) maddeler NavGroup ile açılır menü olur. Rol filtresi hem
// üst maddede hem alt maddede uygulanır (bkz. filterNav); grubun görünür tek alt
// maddesi kalırsa düz linke indirgenir, hiç kalmazsa grup gizlenir.
const NAV = [
  { label: "nav.dashboard", path: "/dashboard", icon: "dashboard", roles: ["ADMIN", "MANAGER", "STAFF"] },
  {
    label: "nav.salonManagement",
    icon: "storefront",
    roles: ["ADMIN"],
    children: [
      { label: "nav.salons", path: "/salons", icon: "format_list_bulleted" },
      { label: "nav.onboarding", path: "/admin/onboarding", icon: "add_business" },
      { label: "nav.packages", path: "/admin/packages", icon: "workspace_premium" },
      { label: "nav.subscriptionRequests", path: "/admin/subscription-requests", icon: "fact_check" },
      { label: "nav.hierarchy", path: "/admin/hierarchy", icon: "account_tree" },
      { label: "nav.systemAdmins", path: "/admin/admins", icon: "admin_panel_settings" },
      { label: "nav.systemSettings", path: "/admin/settings", icon: "tune" },
    ],
  },

  // Üst seviye (sık kullanılan, tek tık)
  { label: "nav.appointments", path: "/appointments", icon: "calendar_today", roles: ["MANAGER", "STAFF"] },
  // Salon & Hesap: Randevunun hemen altında. Salonla ilgili "Hizmetler" burada.
  // STAFF'ta yalnız Hizmetler görünür (grup tek çocuğa inip düz linke dönüşür).
  {
    label: "nav.group.account",
    icon: "storefront",
    roles: ["MANAGER", "STAFF"],
    children: [
      { label: "nav.services", path: "/services", icon: "content_cut", roles: ["MANAGER", "STAFF"] },
      { label: "nav.mySalon", path: "/salons", icon: "storefront", roles: ["MANAGER"] },
      { label: "nav.subscription", path: "/subscription", icon: "verified_user", roles: ["MANAGER"] },
    ],
  },
  { label: "nav.cash", path: "/cash", icon: "point_of_sale", roles: ["MANAGER"] },
  // Personel: ADMIN için üst seviye kalır (MANAGER'da "Ekip Personel" grubunda).
  { label: "nav.staff", path: "/staff", icon: "badge", roles: ["ADMIN"] },

  // Kategoriler (MANAGER menüsü sadeleştirme)
  {
    label: "nav.group.crm",
    icon: "diversity_3",
    roles: ["MANAGER", "STAFF"],
    children: [
      { label: "nav.customers", path: "/customers", icon: "group", roles: ["MANAGER", "STAFF"] },
      { label: "nav.campaigns", path: "/campaigns", icon: "local_offer", roles: ["MANAGER"] },
      { label: "nav.loyalty", path: "/loyalty", icon: "loyalty", roles: ["MANAGER"] },
    ],
  },
  // Stok: başlı başına üst seviye menü.
  { label: "nav.inventory", path: "/inventory", icon: "inventory_2", roles: ["MANAGER", "STAFF"] },
  {
    label: "nav.group.team",
    icon: "badge",
    roles: ["MANAGER"],
    children: [
      { label: "nav.staff", path: "/staff", icon: "badge", roles: ["MANAGER"] },
      { label: "nav.payroll", path: "/payroll", icon: "payments", roles: ["MANAGER"] },
    ],
  },
  {
    label: "nav.group.finance",
    icon: "account_balance_wallet",
    roles: ["MANAGER", "STAFF"],
    children: [
      { label: "nav.invoices", path: "/invoices", icon: "receipt_long", roles: ["MANAGER", "STAFF"] },
      { label: "nav.finance", path: "/finance", icon: "insights", roles: ["MANAGER"] },
    ],
  },
  // Görevler: Finans grubunun hemen altında.
  { label: "nav.tasks", path: "/tasks", icon: "checklist", roles: ["MANAGER", "STAFF"] },

  // Dökümanlar: sistem kılavuzları (ADMIN) + salon dökümanları (MANAGER/STAFF).
  { label: "nav.documents", path: "/documents", icon: "folder_open", roles: ["ADMIN", "MANAGER", "STAFF"] },

  { label: "nav.reports", path: "/reports", icon: "assessment", roles: ["ADMIN", "MANAGER", "STAFF"] },
  {
    label: "nav.devNotes",
    icon: "construction",
    roles: ["ADMIN", "MANAGER", "STAFF"],
    children: [
      { label: "nav.versions", path: "/dev-notes/versions", icon: "history" },
      { label: "nav.roadmap", path: "/dev-notes/roadmap", icon: "track_changes" },
    ],
  },
];

/**
 * NAV'ı role göre filtreler: üst madde + alt madde (children) rolleri uygulanır.
 * Grubun görünür tek alt maddesi kalırsa düz linke indirgenir; hiç kalmazsa
 * grup elenir. Böylece STAFF'ta yarım/tek-elemanlı açılır menüler oluşmaz.
 */
function filterNav(items, role) {
  return items
    .filter((item) => !item.roles || item.roles.includes(role))
    .map((item) => {
      if (!item.children) return item;
      const kids = item.children.filter((c) => !c.roles || c.roles.includes(role));
      if (kids.length === 0) return null;
      if (kids.length === 1) {
        const only = kids[0];
        return { label: only.label, path: only.path, icon: only.icon };
      }
      return { ...item, children: kids };
    })
    .filter(Boolean);
}

function NavGroup({ item, collapsed, onExpand }) {
  const location = useLocation();
  const { t } = useI18n();
  const hasActiveChild = item.children.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (collapsed) {
      setOpen(false);
    } else if (hasActiveChild) {
      setOpen(true);
    }
  }, [collapsed, hasActiveChild]);

  const handleToggle = (e) => {
    e.preventDefault();
    if (collapsed) {
      onExpand();
      setOpen(true);
    } else {
      setOpen((v) => !v);
    }
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center ${
      collapsed ? "justify-center px-0" : "gap-3 px-3"
    } py-2.5 rounded-xl text-sm font-medium transition-all ${
      isActive
        ? "text-primary bg-surface-container-low border-l-4 border-primary"
        : "text-on-surface-variant hover:text-primary hover:bg-surface-container-high"
    }`;

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full flex items-center ${
          collapsed ? "justify-center px-0" : "gap-3 px-3"
        } py-2.5 rounded-xl text-sm font-medium transition-all ${
          hasActiveChild
            ? "text-primary"
            : "text-on-surface-variant hover:text-primary hover:bg-surface-container-high"
        }`}
      >
        <Icon name={item.icon} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left truncate">{t(item.label)}</span>
            <Icon
              name="expand_more"
              className={`!text-[20px] transition-transform ${open ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>
      {open && !collapsed && (
        <div className="mt-1 space-y-1 pl-4">
          {item.children.map((child) => (
            <NavLink key={child.path} to={child.path} end className={navLinkClass}>
              <Icon name={child.icon} className="!text-[20px]" />
              {!collapsed && <span className="truncate">{t(child.label)}</span>}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const visible = filterNav(NAV, user?.role);
  const isGold = (user?.packageLevel ?? 0) >= 3;

  const [profileOpen, setProfileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });
  const dropdownRef = useRef(null);

  // --- Mobil çekmece (drawer) ---
  // lg (1024px) altında sidebar ekran dışında durur, hamburger ile açılır.
  // isDesktop, masaüstüne özel inline stillerin (width/marginLeft) yalnızca
  // geniş ekranda uygulanmasını sağlar; mobilde görünürlük translate ile yönetilir.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setMobileOpen(false); // masaüstüne geçince drawer kapansın
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Rota değişince mobil çekmeceyi kapat (bir menü linkine tıklandığında).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Heartbeat to report online status for active logins
  useEffect(() => {
    if (!user) return;

    const reportHeartbeat = () => {
      activityService.heartbeat().catch((err) => {
        console.debug("Activity heartbeat failed:", err);
      });
    };

    // Report immediately and then every 30 seconds
    reportHeartbeat();
    const interval = setInterval(reportHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Click outside for profile dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  // --- Sürüklenebilir Sol Menü Genişliği (Resize Sidebar) ---
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("sidebarWidth");
    return saved ? Math.min(440, Math.max(200, Number(saved))) : 264;
  });
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent) => {
      if (isResizing) {
        const newWidth = Math.min(440, Math.max(200, mouseMoveEvent.clientX));
        setSidebarWidth(newWidth);
        localStorage.setItem("sidebarWidth", String(newWidth));
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing, resize, stopResizing]);

  // Daraltılmış "rail" modu yalnızca masaüstünde geçerli; mobil çekmece her zaman
  // tam genişlik (etiketler görünür) açılır.
  const railCollapsed = collapsed && isDesktop;

  const dynamicLinkClass = ({ isActive }) =>
    `flex items-center ${
      railCollapsed ? "justify-center px-0" : "gap-3 px-3"
    } py-2.5 rounded-xl text-sm font-medium transition-all ${
      isActive
        ? "text-primary bg-surface-container-low border-l-4 border-primary"
        : "text-on-surface-variant hover:text-primary hover:bg-surface-container-high"
    }`;

  return (
    <div className="min-h-screen flex bg-bg-subtle transition-all duration-300">
      {/* Mobil çekmece açıkken arka karartma (overlay) — tıklayınca kapanır */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        style={{ width: isDesktop ? (collapsed ? 72 : sidebarWidth) : 288 }}
        className={`fixed h-full left-0 top-0 bg-surface-card border-r border-outline-variant flex flex-col py-6 px-4 z-50 select-none ${
          isResizing ? "" : "transition-[width,transform] duration-300"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Sidebar Header */}
        <div className="mb-8 px-3 flex items-center justify-between min-h-[40px]">
          {!railCollapsed ? (
            <>
              <h1 className="text-xl font-bold text-primary flex items-center gap-2 truncate">
                <Icon name="spa" filled /> SalonVIP
              </h1>
              {/* Masaüstü: menüyü daralt */}
              <button
                onClick={toggleSidebar}
                className="hidden lg:block p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition"
                title={t("sidebar.collapse")}
              >
                <Icon name="menu_open" className="!text-[20px]" />
              </button>
              {/* Mobil: çekmeceyi kapat */}
              <button
                onClick={() => setMobileOpen(false)}
                className="lg:hidden p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition"
                aria-label="Menüyü kapat"
              >
                <Icon name="close" className="!text-[20px]" />
              </button>
            </>
          ) : (
            <div className="w-full flex justify-center">
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
                title={t("sidebar.expand")}
              >
                <Icon name="menu" className="!text-[20px]" />
              </button>
            </div>
          )}
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
          {visible.map((item) =>
            item.children ? (
              <NavGroup
                key={item.label}
                item={item}
                collapsed={railCollapsed}
                onExpand={() => setCollapsed(false)}
              />
            ) : (
              <NavLink key={item.path} to={item.path} className={dynamicLinkClass}>
                <Icon name={item.icon} />
                {!railCollapsed && <span className="truncate">{t(item.label)}</span>}
              </NavLink>
            )
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="pt-4 border-t border-outline-variant text-center">
          {railCollapsed ? (
            <div className="relative group inline-block">
              <Icon
                name="bug_report"
                className="!text-[18px] text-on-surface-variant/70 hover:text-error transition cursor-pointer"
              />
              <div className="absolute left-10 bottom-0 hidden group-hover:block bg-surface-card border border-outline-variant rounded-xl p-3 shadow-lg text-left z-50 w-48 pointer-events-auto">
                <p className="text-[10px] font-bold text-error uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Icon name="bug_report" className="!text-[12px]" /> Sorun Bildir
                </p>
                <a
                  href="mailto:bilalykt21@gmail.com"
                  className="block text-[10px] text-on-surface-variant hover:text-primary transition font-medium break-all mb-0.5"
                >
                  bilalykt21@gmail.com
                </a>
                <a
                  href="mailto:harunyakut2156@gmail.com"
                  className="block text-[10px] text-on-surface-variant hover:text-primary transition font-medium break-all"
                >
                  harunyakut2156@gmail.com
                </a>
              </div>
            </div>
          ) : (
            <div className="text-left px-2">
              <div className="flex items-center gap-1.5 mb-1 text-error">
                <Icon name="bug_report" className="!text-[14px]" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Sorun Bildir</span>
              </div>
              <div className="space-y-0.5 pl-5">
                <a
                  href="mailto:bilalykt21@gmail.com"
                  className="block text-[10px] text-on-surface-variant hover:text-primary transition font-medium"
                >
                  bilalykt21@gmail.com
                </a>
                <a
                  href="mailto:harunyakut2156@gmail.com"
                  className="block text-[10px] text-on-surface-variant hover:text-primary transition font-medium"
                >
                  harunyakut2156@gmail.com
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Sürükle & Genişlet Çubuğu (Resize Handle) — yalnızca masaüstü */}
        {!collapsed && isDesktop && (
          <div
            onMouseDown={startResizing}
            className="absolute top-0 right-0 w-2.5 h-full cursor-col-resize hover:bg-primary/20 active:bg-primary/40 z-50 group hidden lg:flex items-center justify-center transition-colors"
            title="Sürükleyerek menü genişliğini ayarlayın"
          >
            <div className="w-1 h-12 bg-outline-variant/60 group-hover:bg-primary group-active:bg-primary rounded-full transition-colors" />
          </div>
        )}
      </aside>

      {/* Main Container */}
      <div
        className={`flex-1 flex flex-col min-w-0 ${
          isResizing ? "" : "transition-[margin] duration-300"
        }`}
        style={{ marginLeft: isDesktop ? (collapsed ? "72px" : `${sidebarWidth}px`) : 0 }}
      >
        <header className="sticky top-0 z-30 h-16 bg-surface-card border-b border-outline-variant flex items-center px-4 sm:px-6 gap-3">
          {/* Mobil: menüyü aç (hamburger) */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 -ml-1 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition"
            aria-label="Menüyü aç"
          >
            <Icon name="menu" />
          </button>
          {/* Mobil: uygulama adı (sidebar kapalıyken kimlik) */}
          <span className="lg:hidden font-bold text-primary flex items-center gap-1.5 truncate">
            <Icon name="spa" filled className="!text-[20px]" /> SalonVIP
          </span>

          {/* Profile Dropdown */}
          <div className="relative ml-auto" ref={dropdownRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-3 hover:bg-surface-container-high p-1.5 px-3 rounded-2xl transition text-left outline-none"
            >
              <div className="text-right leading-tight hidden sm:block">
                <p className="text-sm font-semibold text-on-surface">{user?.fullName ?? user?.email}</p>
                <p className="text-xs text-on-surface-variant font-medium">{user?.role}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold border border-primary/20">
                {(user?.fullName ?? user?.email ?? "?").charAt(0).toUpperCase()}
              </div>
              <Icon
                name="expand_more"
                className={`!text-[18px] text-on-surface-variant transition-transform ${
                  profileOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-3xl shadow-modal border border-outline-variant py-3.5 z-50 animate-slide-in">
                {/* User Identity Display */}
                <div className="px-5 py-2.5 border-b border-outline-variant/30 mb-2">
                  <p className="text-sm font-bold text-on-surface truncate">{user?.fullName ?? user?.email}</p>
                  <p className="text-[11px] font-bold text-primary uppercase tracking-wider mt-0.5">{user?.role}</p>
                </div>

                {/* Section: Görünüm & Dil */}
                <div className="px-2 py-1">
                  <span className="px-4 text-[9px] font-bold text-on-surface-variant/50 uppercase tracking-widest block mb-1">
                    {t("profile.category.appearance")}
                  </span>
                  
                  <NavLink
                    to="/settings#theme"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group"
                  >
                    <Icon name="palette" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                    <span>{t("profile.settings.theme")}</span>
                  </NavLink>

                  <NavLink
                    to="/settings#language"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group"
                  >
                    <Icon name="language" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                    <span>{t("profile.settings.lang")}</span>
                  </NavLink>
                </div>

                <div className="border-t border-outline-variant/30 my-2"></div>

                {/* Section: Güvenlik & Şifre */}
                <div className="px-2 py-1">
                  <span className="px-4 text-[9px] font-bold text-on-surface-variant/50 uppercase tracking-widest block mb-1">
                    {t("profile.category.security")}
                  </span>

                  <NavLink
                    to="/change-password"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group"
                  >
                    <Icon name="key" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                    <span>{t("profile.changePassword")}</span>
                  </NavLink>

                  {user?.role === "MANAGER" && (
                    <NavLink
                      to="/cash-password"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group"
                    >
                      <Icon name="point_of_sale" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                      <span>{t("profile.cashPassword")}</span>
                    </NavLink>
                  )}

                  {isGold && (
                    <NavLink
                      to="/settings#2fa"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group"
                    >
                      <Icon name="verified_user" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                      <span>{t("profile.settings.2fa")}</span>
                    </NavLink>
                  )}
                </div>

                <div className="border-t border-outline-variant/30 my-2"></div>

                {/* Section: Oturum */}
                <div className="px-2 py-1">
                  <span className="px-4 text-[9px] font-bold text-on-surface-variant/50 uppercase tracking-widest block mb-1">
                    {t("profile.category.session")}
                  </span>

                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      if (user?.email) {
                        localStorage.setItem("lockedUserEmail", user.email);
                        localStorage.setItem("lockedUserName", user.fullName ?? user.email);
                      }
                      dispatch(logout());
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-on-surface hover:bg-primary/5 hover:text-primary transition-all group text-left"
                  >
                    <Icon name="lock" className="!text-[18px] text-on-surface-variant/70 group-hover:text-primary transition-colors" />
                    <span>{t("profile.lock")}</span>
                  </button>

                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      dispatch(logout());
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-xs font-semibold text-error hover:bg-error/5 transition-all group text-left mt-0.5"
                  >
                    <Icon name="logout" className="!text-[18px] text-error group-hover:scale-105 transition-transform" />
                    <span>{t("profile.logout")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1440px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
